import { all, takeLatest, takeLeading, call, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

import {
  requestViewPersonDetails,
  requestViewPersonProfile,
  requestUpdatePersonDetails,
  requestUpdatePersonEmail,
  requestUpdatePersonUsername,
  requestUpdateProfilePicture,
  requestViewAllFacilitators,
  requestSearchOrganisation,
  requestResetPasswordInternal,
  requestAddRepresentative,
  requestDeleteUserDetails,
} from './party.actions';
import {
  getViewPersonDetailsRequest,
  getViewPersonProfileRequest,
  getUpdatePersonDetailsRequest,
  getUpdatePersonEmailRequest,
  getUpdatePersonUsernameRequest,
  getUpdateProfilePictureRequest,
  getViewAllFacilitatorsRequest,
  getSearchOrganisationRequest,
  getResetPasswordInternalRequest,
  getAddRepresentativeRequest,
  getDeleteUserDetailsRequest,
} from './party.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import {
  setDetails,
  setProfile,
  setFacilitators,
  setOrganisation,
  setAdminRole,
  setUserGroups,
} from '../../slices/party/party.slice';

export function* performViewPersonDetailsRequest({ payload }) {
  const { personExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Person Details'));

    const [endpoint, options] = getViewPersonDetailsRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    delete data.message;

    yield put(setDetails(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Person Details'));
  }
}

export function* watchForViewPersonDetailsRequest() {
  yield takeLatest(requestViewPersonDetails.type, performViewPersonDetailsRequest);
}

export function* performViewPersonProfileRequest({ payload }) {
  const { personExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Person Profile'));

    const [endpoint, options] = getViewPersonProfileRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    const { belongToGroups } = data;

    if (belongToGroups && belongToGroups.length > 0) {
      if (belongToGroups.includes('admin')) {
        yield put(setAdminRole(true));
      }
    } else {
      yield put(setAdminRole(undefined));
    }

    yield put(setUserGroups(belongToGroups));

    delete data.message;

    yield put(setProfile(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Person Profile'));
  }
}

export function* watchForViewPersonProfileRequest() {
  yield takeLeading(requestViewPersonProfile.type, performViewPersonProfileRequest);
}

export function* performUpdatePersonDetailsRequest({ payload }) {
  const { details } = payload;

  try {
    yield put(addSpinner('Updating Person Details'));

    const [endpoint, options] = getUpdatePersonDetailsRequest(details);

    const { data } = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewPersonProfile({ personExternalReference: details.personExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Person Details'));
  }
}

export function* watchForUpdatePersonDetailsRequest() {
  yield takeLatest(requestUpdatePersonDetails.type, performUpdatePersonDetailsRequest);
}

export function* performUpdatePersonUsernameRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Person Username'));

    const [endpoint, options] = getUpdatePersonUsernameRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewPersonProfile({ personExternalReference: input.personExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Person Username'));
  }
}

export function* watchForUpdatePersonUsernameRequest() {
  yield takeLatest(requestUpdatePersonUsername.type, performUpdatePersonUsernameRequest);
}

export function* performUpdatePersonEmailRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Person Email'));

    const [endpoint, options] = getUpdatePersonEmailRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewPersonProfile({ personExternalReference: input.personExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Person Email'));
  }
}

export function* watchForUpdatePersonEmailRequest() {
  yield takeLatest(requestUpdatePersonEmail.type, performUpdatePersonEmailRequest);
}

export function* performUpdateProfilePictureRequest({ payload }) {
  const { input } = payload;

  try {
    yield put(addSpinner('Updating Profile Picture'));

    const [endpoint, options] = getUpdateProfilePictureRequest(input);

    yield call(axios, endpoint, options);

    yield put(requestViewPersonProfile({ personExternalReference: input.personExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Profile Picture'));
  }
}

export function* watchForUpdateProfilePictureRequest() {
  yield takeLatest(requestUpdateProfilePicture.type, performUpdateProfilePictureRequest);
}

export function* performViewAllFacilitatorsRequest() {
  try {
    yield put(addSpinner('Retrieving Facilitators'));

    const [endpoint, options] = getViewAllFacilitatorsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setFacilitators(data.facilitators));
  } catch (error) {
    yield put(setFacilitators([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Facilitators'));
  }
}

export function* watchForViewAllFacilitatorsRequest() {
  yield takeLatest(requestViewAllFacilitators.type, performViewAllFacilitatorsRequest);
}

export function* performSearchOrganisationRequest({ payload }) {
  const { onSuccess, companyRegistrationNumber } = payload;

  try {
    yield put(addSpinner('Searching Organisation'));

    const [endpoint, options] = getSearchOrganisationRequest(companyRegistrationNumber);

    const { data } = yield call(axios, endpoint, options);

    yield put(setOrganisation(data.organisation));

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Searching Organisation'));
  }
}

export function* watchForSearchOrganisationRequest() {
  yield takeLeading(requestSearchOrganisation.type, performSearchOrganisationRequest);
}

export function* performResetPasswordInternalRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating password'));

    const [endpoint, options] = getResetPasswordInternalRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating password'));
  }
}

export function* watchForResetPasswordInternalRequest() {
  yield takeLatest(requestResetPasswordInternal.type, performResetPasswordInternalRequest);
}

export function* performAddRepresentativeRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Adding Representative'));

    const [endpoint, options] = getAddRepresentativeRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Adding Representative'));
  }
}

export function* watchForAddRepresentativeRequest() {
  yield takeLatest(requestAddRepresentative.type, performAddRepresentativeRequest);
}

export function* performDeleteUserDetails({ payload }) {
  const { personExternalReference, email, onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Account'));

    const [endpoint, options] = yield call(getDeleteUserDetailsRequest, {
      email,
      personExternalReference,
    });

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Deleting Account'));
  }
}

export function* watchForDeleteUserDetails() {
  yield takeLatest(requestDeleteUserDetails.type, performDeleteUserDetails);
}

export default function* partySaga() {
  yield all([
    watchForViewPersonDetailsRequest(),
    watchForViewPersonProfileRequest(),
    watchForUpdatePersonDetailsRequest(),
    watchForUpdatePersonUsernameRequest(),
    watchForUpdatePersonEmailRequest(),
    watchForUpdateProfilePictureRequest(),
    watchForViewAllFacilitatorsRequest(),
    watchForSearchOrganisationRequest(),
    watchForResetPasswordInternalRequest(),
    watchForAddRepresentativeRequest(),
    watchForDeleteUserDetails(),
  ]);
}
